import React, { useState, useEffect } from 'react';
import { sellerProfileAPI } from '../../services/api';
import './SellerProfiles.css';

const SellerProfiles = () => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [formData, setFormData] = useState({
    profile_name: '',
    seller_name: '',
    seller_address: '',
    seller_gstin: '',
    seller_contact: '',
    is_default: false,
  });

  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    try {
      const response = await sellerProfileAPI.getAll();
      if (response.success) {
        setProfiles(response.data);
      }
    } catch (err) {
      alert('Failed to fetch seller profiles');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingProfile) {
        const response = await sellerProfileAPI.update(editingProfile.id, formData);
        if (response.success) {
          alert('Seller profile updated successfully');
          fetchProfiles();
          resetForm();
        }
      } else {
        const response = await sellerProfileAPI.create(formData);
        if (response.success) {
          alert('Seller profile created successfully');
          fetchProfiles();
          resetForm();
        }
      }
    } catch (err) {
      alert('Failed to save seller profile');
    }
  };

  const handleEdit = (profile) => {
    setEditingProfile(profile);
    setFormData({
      profile_name: profile.profile_name,
      seller_name: profile.seller_name,
      seller_address: profile.seller_address,
      seller_gstin: profile.seller_gstin || '',
      seller_contact: profile.seller_contact || '',
      is_default: profile.is_default,
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this seller profile?')) {
      try {
        const response = await sellerProfileAPI.delete(id);
        if (response.success) {
          alert('Seller profile deleted successfully');
          fetchProfiles();
        }
      } catch (err) {
        alert('Failed to delete seller profile');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      profile_name: '',
      seller_name: '',
      seller_address: '',
      seller_gstin: '',
      seller_contact: '',
      is_default: false,
    });
    setEditingProfile(null);
    setShowForm(false);
  };

  if (loading) {
    return <div className="loading">Loading seller profiles...</div>;
  }

  return (
    <div className="seller-profiles-container">
      <div className="profiles-header">
        <h2>Seller Profiles</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          className="create-btn"
        >
          {showForm ? 'Cancel' : '+ Add Profile'}
        </button>
      </div>

      {showForm && (
        <div className="profile-form-card">
          <h3>{editingProfile ? 'Edit' : 'Create'} Seller Profile</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-field">
                <label>Profile Name *</label>
                <input
                  type="text"
                  name="profile_name"
                  value={formData.profile_name}
                  onChange={handleInputChange}
                  placeholder="e.g., My Company, Branch Office"
                  required
                />
              </div>
              <div className="form-field">
                <label>
                  <input
                    type="checkbox"
                    name="is_default"
                    checked={formData.is_default}
                    onChange={handleInputChange}
                  />
                  {' '}Set as Default
                </label>
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label>Business/Seller Name *</label>
                <input
                  type="text"
                  name="seller_name"
                  value={formData.seller_name}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-field">
                <label>GSTIN</label>
                <input
                  type="text"
                  name="seller_gstin"
                  value={formData.seller_gstin}
                  onChange={handleInputChange}
                  maxLength="15"
                  placeholder="15-digit GSTIN"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-field full-width">
                <label>Address *</label>
                <textarea
                  name="seller_address"
                  value={formData.seller_address}
                  onChange={handleInputChange}
                  required
                  rows="3"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label>Contact (Phone/Email)</label>
                <input
                  type="text"
                  name="seller_contact"
                  value={formData.seller_contact}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-actions">
              <button type="button" onClick={resetForm} className="cancel-btn">
                Cancel
              </button>
              <button type="submit" className="submit-btn">
                {editingProfile ? 'Update' : 'Create'} Profile
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="profiles-grid">
        {profiles.length === 0 ? (
          <div className="empty-state">
            <p>No seller profiles found. Create your first profile!</p>
          </div>
        ) : (
          profiles.map((profile) => (
            <div key={profile.id} className="profile-card">
              {profile.is_default && <span className="default-badge">Default</span>}
              <h3>{profile.profile_name}</h3>
              <div className="profile-details">
                <p><strong>{profile.seller_name}</strong></p>
                <p className="address">{profile.seller_address}</p>
                {profile.seller_gstin && <p>GSTIN: {profile.seller_gstin}</p>}
                {profile.seller_contact && <p>Contact: {profile.seller_contact}</p>}
              </div>
              <div className="profile-actions">
                <button onClick={() => handleEdit(profile)} className="edit-btn">
                  Edit
                </button>
                <button onClick={() => handleDelete(profile.id)} className="delete-btn">
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default SellerProfiles;
